/**
 * BoardContent.js
 *
 * Content section for travel board cards.
 * Displays board-specific information like item count, type breakdown.
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { getTypeBadge } from '../../../utils/normalizeCardData';
import Icon from '../../shared/Icon';

// Content-type → catalog key stem (feedCard: boardContent.typeBreakdown.<stem>).
// Non-translatable icon lookups still go through getTypeBadge(type).
const TYPE_BREAKDOWN_KEYS = {
  activity: 'activity',
  trip: 'trip',
  album: 'album',
  collection: 'collection',
  walking_tour: 'walkingTour'
};

/**
 * Type breakdown display - shows count of each content type in board
 */
const TypeBreakdown = ({ breakdown }) => {
  const { t } = useTranslation('feedCard');
  if (!breakdown || Object.keys(breakdown).length === 0) return null;

  const entries = Object.entries(breakdown).filter(([_, count]) => count > 0);
  if (entries.length === 0) return null;

  const getLabel = (type, count) => {
    const keyStem = TYPE_BREAKDOWN_KEYS[type];
    if (!keyStem) return `${count} ${type}`;
    return t(`boardContent.typeBreakdown.${keyStem}`, { count });
  };

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-content-secondary mb-2">
      {entries.map(([type, count]) => {
        const badge = getTypeBadge(type);
        return (
          <span key={type} className="flex items-center">
            <Icon name={badge?.icon || 'clipboard'} className="w-3 h-3 inline mr-1" />
            <span>{getLabel(type, count)}</span>
          </span>
        );
      })}
    </div>
  );
};

/**
 * Main BoardContent Component
 *
 * Props:
 * @param {Object} data - Normalized card data
 */
const BoardContent = ({ data }) => {
  const { t } = useTranslation('feedCard');
  const { title, description, typeData } = data;
  const {
    itemCount = 0,
    typeBreakdown,
    visibility
  } = typeData || {};

  const visibilityIconName = visibility === 'private' ? 'lock' : visibility === 'friends' ? 'userGroup' : 'globe';
  const visibilityTitle = visibility === 'private' ? t('boardContent.visibility.private') : visibility === 'friends' ? t('boardContent.visibility.friends') : t('boardContent.visibility.public');

  return (
    <div className="flex-grow">
      {/* Title + visibility */}
      <h3 className="font-semibold text-content-primary mb-2 line-clamp-1 flex items-center gap-1.5">
        <span className="truncate">{title}</span>
        <span className="flex-shrink-0 text-xs text-content-muted" title={visibilityTitle}>
          <Icon name={visibilityIconName} className="w-3.5 h-3.5" />
        </span>
      </h3>

      {/* Board stats */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-content-secondary mb-3">
        {itemCount > 0 && (
          <span className="flex items-center">
            <Icon name="clipboard" className="w-3.5 h-3.5 mr-1" />
            {t('boardContent.itemCount', { count: itemCount })}
          </span>
        )}
      </div>

      {/* Type breakdown */}
      <TypeBreakdown breakdown={typeBreakdown} />

      {/* Description */}
      {description && (
        <p className="text-sm text-content-secondary mb-3 line-clamp-2">
          {description}
        </p>
      )}
    </div>
  );
};

export default memo(BoardContent);
